/**
 * components/TransformReceiptModal.js
 * Read-only "Tinjau Transformasi" review modal. Displays the validated transaction (type,
 * bucket, input/output lines, before→after stock preview) before the user commits.
 * Mutates nothing — onConfirm triggers the real engine run; onClose cancels back to editing.
 */
import React from "react";
import { fmtQty } from "../utils/idGenerators";
import { bucketLabel } from "../utils/transformations";
import { useModalEsc, backdropClose } from "../utils/modalStack";

const TYPE_LABEL = { ASSEMBLE: "Rakit (Assemble)", TEARDOWN: "Urai (Teardown)" };

export default function TransformReceiptModal({
  type, bucket, inputLines, outputLines, preview, byCode, pendingCodes = new Set(), onConfirm, onClose,
}) {
  useModalEsc(onClose);

  const renderLines = (lines) =>
    lines.map((l, i) => {
      const it = byCode.get(l.internalCode);
      const name = it ? it.name : l.internalCode;
      return (
        <li key={i}>
          <span className="mono">{l.internalCode}</span> · {name}
          {pendingCodes.has(l.internalCode) && <span className="chip chip--baru" style={{ marginLeft: "var(--gap-sm)" }}>baru</span>}
          {l.substituteFor && <span className="chip chip--muted" style={{ marginLeft: "var(--gap-sm)" }}>Substitusi untuk {l.substituteFor}</span>}
          {" "}× <b>{fmtQty(l.qty)}</b>
        </li>
      );
    });

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" onMouseDown={backdropClose(onClose)}>
      <div className="modal-box" onMouseDown={(e) => e.stopPropagation()}>
        <h2 className="modal-title">Tinjau Transformasi</h2>
        <div className="page-sub" style={{ marginBottom: "var(--gap-lg)" }}>
          {TYPE_LABEL[type] || type} · {bucketLabel(bucket)}
        </div>

        <div className="form-label">Input</div>
        <ul className="recipe-lines">{renderLines(inputLines)}</ul>

        <div className="form-label" style={{ marginTop: "var(--gap-md)" }}>Output</div>
        <ul className="recipe-lines">{renderLines(outputLines)}</ul>
        {outputLines.some((l) => pendingCodes.has(l.internalCode)) && (
          <div className="form-hint" style={{ marginTop: "var(--gap-sm)" }}>
            Item bertanda <span className="chip chip--baru">baru</span> akan dibuat saat Anda menekan Konfirmasi.
          </div>
        )}

        {preview.length > 0 && (
          <div style={{ marginTop: "var(--gap-lg)" }}>
            <div className="card__title">Pratinjau perubahan stok ({bucketLabel(bucket)})</div>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Barang</th>
                    <th className="num">Sebelum</th>
                    <th className="num">Perubahan</th>
                    <th className="num">Sesudah</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.map((p) => (
                    <tr key={p.code} style={{ cursor: "default" }}>
                      <td><span className="mono">{p.code}</span> · {p.name}</td>
                      <td className="num">{fmtQty(p.before)}</td>
                      <td className="num" style={{ color: p.delta >= 0 ? "var(--green)" : "var(--red)" }}>
                        {p.delta > 0 ? `+${p.delta}` : p.delta}
                      </td>
                      <td className="num total-cell" style={{ color: p.short ? "var(--red)" : undefined }}>
                        {fmtQty(p.after)}{p.short ? " ⚠" : ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {preview.some((p) => p.short) && (
              <div className="form-hint">
                ⚠ Sebagian stok akan menjadi negatif. Ini diperbolehkan (mis. stok pinjaman) dan
                transformasi tetap akan berjalan.
              </div>
            )}
          </div>
        )}

        <div className="modal-actions">
          <button type="button" className="btn" onClick={onClose}>Batal</button>
          <button type="button" className="btn btn-primary" onClick={onConfirm}>Konfirmasi Transformasi</button>
        </div>
      </div>
    </div>
  );
}
